// UPPDRAG-40 — reusable rich-text editor backed by TipTap StarterKit.
//
// Toolbar exposes the formatting tags we sanitize-allow downstream:
// bold, italic, headings (H2/H3), bullet list, ordered list, blockquote.
// The editor's HTML output is the source of truth — we trust TipTap's
// serializer here and rely on sanitizeRichText() at READ time on the
// player side to strip anything malicious that might slip in via a
// different write path (admin pasting copied HTML, future API import).
//
// Drop-in replacement for <Textarea> in admin forms:
//   <RichTextEditor value={field.value ?? ""} onChange={field.onChange} />

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote } from "lucide-react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Min editor height in px. Defaults to 160. */
  minHeightPx?: number;
  /** Disable typing/toolbar. */
  disabled?: boolean;
  /** Override the wrapper class. */
  className?: string;
}

export function RichTextEditor({
  value,
  onChange,
  minHeightPx = 160,
  disabled = false,
  className,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      // StarterKit bundles paragraph, heading (h1-h6), bold, italic,
      // strike, code, listItem, bulletList, orderedList, blockquote,
      // hardBreak, horizontalRule, history, dropcursor, gapcursor.
      // No link extension by default — link sanitization adds risk we
      // skip for v1.
      StarterKit.configure({
        // Restrict heading levels to H2/H3. H1 is reserved for the page
        // title in the layout chrome.
        heading: { levels: [2, 3] },
      }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap renders an empty doc as "<p></p>". Surface that as "" so
      // the form's saveMutation can persist NULL via `values.x || null`.
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-3 py-2",
          "prose-headings:font-semibold prose-h2:text-base prose-h3:text-sm",
          "prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-blockquote:my-2",
        ),
      },
    },
  });

  // Sync external value → editor (e.g. when the form resets after save).
  // editor.commands.setContent uses the same serializer so it round-trips.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "<p></p>";
    if (current !== incoming) {
      editor.commands.setContent(incoming, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={cn("rounded-md border bg-background", className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b p-1">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          aria-label="Fet"
          disabled={disabled}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-label="Kursiv"
          disabled={disabled}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          aria-label="Rubrik 2"
          disabled={disabled}
        >
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          aria-label="Rubrik 3"
          disabled={disabled}
        >
          <Heading3 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-label="Punktlista"
          disabled={disabled}
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-label="Numrerad lista"
          disabled={disabled}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          aria-label="Citat"
          disabled={disabled}
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>

      {/* Editor surface */}
      <EditorContent editor={editor} style={{ minHeight: `${minHeightPx}px` }} />
    </div>
  );
}

interface ToolbarButtonProps extends React.ComponentProps<typeof Button> {
  active?: boolean;
}

function ToolbarButton({ active, className, ...props }: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-7 w-7 p-0",
        active && "bg-accent text-accent-foreground",
        className,
      )}
      {...props}
    />
  );
}
